// src/components/GalleryPage.js
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { auth, db, functions } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
  startAfter,
  getDocs,
  and
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { PAGE_SIZE } from '../constants';
import { userService } from '../services/UserService';
import { groupByDate } from '../utils/groupByDate';
import GalleryThumbnail from './GalleryThumbnail';
import ImagePreviewModal from './ImagePreviewModal';
import NoteDetail from './NoteDetail';
import logger from '../utils/logger';

const GalleryPage = () => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrationChecked, setMigrationChecked] = useState(false);
  const [migrationInProgress, setMigrationInProgress] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [previewState, setPreviewState] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);

  const unsubscribeRef = useRef(null);
  const migrationTriggeredRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Check migration status on mount
  useEffect(() => {
    if (!auth.currentUser) return;
    if (migrationTriggeredRef.current) return;

    const checkMigration = async () => {
      const userData = userService.currentUser;

      if (userData?.migrations?.hasImagesMigrated === true) {
        setMigrationChecked(true);
        return;
      }

      migrationTriggeredRef.current = true;
      setMigrationInProgress(true);
      try {
        const migrateUser = httpsCallable(functions, 'migrateUser');
        await migrateUser({ migrationType: 'hasImages' });
        setMigrationChecked(true);
      } catch (error) {
        logger.error('Gallery migration failed:', error);
        // Proceed anyway — notes that already have hasImages will still show
        setMigrationChecked(true);
      } finally {
        setMigrationInProgress(false);
      }
    };

    checkMigration();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up Firestore listener once migration is checked
  useEffect(() => {
    if (!migrationChecked || !auth.currentUser) return;

    const notesRef = collection(db, 'notes');
    const q = query(
      notesRef,
      and(
        where('uid', '==', auth.currentUser.uid),
        where('isPendingtoDelete', '==', false),
        where('hasImages', '==', true)
      ),
      orderBy('creationDate', 'desc'),
      limit(PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotes(notesData);
      setLoading(false);

      const lastVisible = snapshot.docs[snapshot.docs.length - 1];
      setLastDoc(lastVisible || null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    }, (error) => {
      logger.error('Gallery query error:', error);
      setLoading(false);
    });

    unsubscribeRef.current = unsubscribe;
    return () => unsubscribe();
  }, [migrationChecked]);

  // Load more notes
  const fetchMoreNotes = useCallback(async () => {
    if (!lastDoc || loadingMore || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoadingMore(true);

    try {
      const notesRef = collection(db, 'notes');
      const q = query(
        notesRef,
        and(
          where('uid', '==', auth.currentUser.uid),
          where('isPendingtoDelete', '==', false),
          where('hasImages', '==', true)
        ),
        orderBy('creationDate', 'desc'),
        startAfter(lastDoc),
        limit(PAGE_SIZE)
      );

      const snapshot = await getDocs(q);
      const newNotes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setNotes(prev => {
        const existingIds = new Set(prev.map(n => n.id));
        const uniqueNew = newNotes.filter(n => !existingIds.has(n.id));
        return [...prev, ...uniqueNew];
      });

      const lastVisible = snapshot.docs[snapshot.docs.length - 1];
      setLastDoc(lastVisible || null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (error) {
      logger.error('Gallery fetchMore error:', error);
    } finally {
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [lastDoc, loadingMore]);

  // Build gallery items from notes
  const galleryItems = useMemo(() => {
    const items = [];
    notes.forEach(note => {
      (note.images || []).forEach((imageUID) => {
        items.push({
          imageUID,
          noteId: note.id,
          uid: note.uid,
          emoji: note.emoji,
          title: note.title,
          creationDate: note.creationDate,
          note
        });
      });
    });
    return items;
  }, [notes]);

  // Group gallery items by date
  const groupedSections = useMemo(() => {
    return groupByDate(galleryItems, t);
  }, [galleryItems, t]);

  // Handle image click — open preview
  const handleImageClick = useCallback((sectionItems, indexInSection) => {
    // Build flat list of all gallery items for navigation
    const allImages = galleryItems.map(item => item.imageUID);
    const clickedItem = sectionItems[indexInSection];
    const globalIndex = galleryItems.findIndex(
      item => item.imageUID === clickedItem.imageUID && item.noteId === clickedItem.noteId
    );

    setPreviewState({
      images: allImages,
      initialIndex: globalIndex >= 0 ? globalIndex : 0,
      galleryItems
    });
  }, [galleryItems]);

  // Handle opening linked note from preview
  const handleOpenNote = useCallback((note) => {
    setPreviewState(null);
    setSelectedNote(note);
  }, []);

  // Handle note deletion from modal
  const handleDeleteNote = useCallback((noteId) => {
    setSelectedNote(null);
    // Note will be removed from gallery via onSnapshot listener
  }, []);

  // Loading / migration state
  if (migrationInProgress) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-[#F07807] rounded-full animate-spin mb-4" />
        <p className="text-sm">{t('gallery.migrating')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-[#F07807] rounded-full animate-spin" />
      </div>
    );
  }

  // Empty state
  if (galleryItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <PhotoIcon className="w-16 h-16 mb-4" />
        <p className="text-lg font-medium">{t('gallery.empty')}</p>
        <p className="text-sm mt-1">{t('gallery.emptyDescription')}</p>
      </div>
    );
  }

  return (
    <div className="md:pb-4 md:pt-0 flex flex-col h-full px-2">
      {/* Header — hidden on mobile since App.js shows it */}
      <div className="flex items-center justify-between mb-4 pt-4 md:pt-0 px-0">
        <div className="hidden md:flex items-center gap-x-3">
          <PhotoIcon className="h-8 w-8 text-gray-900" />
          <h1 className="text-3xl font-bold text-gray-900">{t('gallery.title')}</h1>
        </div>
      </div>

      {/* Gallery grid */}
      <div className="flex-1 overflow-y-auto pb-4">
        {groupedSections.map(([sectionName, items]) => (
          items.length > 0 && (
            <div key={sectionName} className="mb-6">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 mt-2">
                {sectionName}
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-1">
                {items.map((item, idx) => (
                  <GalleryThumbnail
                    key={`${item.noteId}-${item.imageUID}`}
                    uid={item.uid}
                    imageUID={item.imageUID}
                    onClick={() => handleImageClick(items, idx)}
                  />
                ))}
              </div>
            </div>
          )
        ))}

        {/* Load more */}
        {hasMore && (
          <div className="flex justify-center py-4">
            <button
              onClick={fetchMoreNotes}
              disabled={loadingMore}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              {loadingMore ? t('common.loading') : t('gallery.loadMore')}
            </button>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewState && (
        <ImagePreviewModal
          images={previewState.images}
          initialIndex={previewState.initialIndex}
          uid={auth.currentUser.uid}
          onClose={() => setPreviewState(null)}
          source="gallery"
          galleryItems={previewState.galleryItems}
          onOpenNote={handleOpenNote}
        />
      )}

      {/* Note Detail Modal */}
      {selectedNote && (
        <NoteDetail
          note={selectedNote}
          onClose={() => setSelectedNote(null)}
          onDelete={handleDeleteNote}
          variant="modal"
        />
      )}
    </div>
  );
};

export default GalleryPage;
